import React, { useState } from "react";
import Button from "../../../components/common/Button";
import Badge from "../../../components/common/Badge";
import {
  UserIcon,
  MailIcon,
  PhoneIcon,
  ShieldCheckIcon,
  ChevronDownIcon,
  LockClosedIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  CogIcon,
  OfficeBuildingIcon,
  KeyIcon,
} from "@heroicons/react/outline";
import { MODULE_LABELS, ROLE_COLORS } from "../../../constants/admin.constants";

const AdminCard = ({
  admin,
  handleSingleAction,
  openResetPasswordModal,
  openPermissionModal,
  openBranchAccessModal, // NEW: Branch access modal handler
  selected,
  currentFilter,
  expandedByDefault = false,
  isRootAdmin,
}) => {
  const [isExpanded, setIsExpanded] = useState(expandedByDefault);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Get role color
  const getRoleColor = (role) => {
    return ROLE_COLORS[role] || "default";
  };

  // Get enabled modules count
  const getEnabledModulesCount = () => {
    if (admin.role === "root") return "All Access";
    if (!admin.accessControl) return "No Access";

    const enabledCount = Object.values(admin.accessControl || {}).filter(
      (module) => module.enabled
    ).length;
    return `${enabledCount} Modules`;
  };

  // Get branch access summary
  const getBranchAccessSummary = () => {
    if (admin.role === "root") return "All Branches";

    const primaryBranch = admin.branchAccess?.primaryBranch;
    const secondaryCount = admin.branchAccess?.secondaryBranches?.length || 0;

    if (!primaryBranch && secondaryCount === 0) {
      return "No Branch Access";
    }

    if (secondaryCount > 0) {
      return `${primaryBranch || "None"} +${secondaryCount}`;
    }

    return primaryBranch || "No Branch";
  };

  // Render action buttons based on current filter
  const renderActionButtons = () => {
    if (currentFilter === "ForgotPasswordRequests") {
      return (
        <Button
          variant="primary"
          onClick={() => openResetPasswordModal(admin)}
          size="sm"
          className="py-1.5 px-3"
        >
          Reset Password
        </Button>
      );
    } else if (currentFilter === "Blocked") {
      return (
        <Button
          variant="success"
          onClick={() => handleSingleAction(admin.adminCode, "unblock")}
          size="sm"
          className="py-1.5 px-3"
        >
          Unblock
        </Button>
      );
    } else {
      return (
        <div className="flex space-x-2">
          {/* Branch Access Button - NEW */}
          {isRootAdmin && admin.role !== "root" && openBranchAccessModal && (
            <Button
              variant="secondary"
              onClick={() => openBranchAccessModal(admin)}
              size="sm"
              className="py-1.5 px-3"
              title="Manage Branch Access"
            >
              <OfficeBuildingIcon className="w-4 h-4" />
            </Button>
          )}
          {isRootAdmin && admin.role !== "root" && (
            <Button
              variant="secondary"
              onClick={() => openPermissionModal(admin)}
              size="sm"
              className="py-1.5 px-3"
              title="Manage Permissions"
            >
              <CogIcon className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="danger"
            onClick={() => handleSingleAction(admin.adminCode, "block")}
            size="sm"
            className="py-1.5 px-3"
          >
            Block
          </Button>
          {admin.role !== "root" && (
            <Button
              variant="danger-text"
              onClick={() => handleSingleAction(admin.adminCode, "delete")}
              size="sm"
              className="py-1.5 px-3"
            >
              Delete
            </Button>
          )}
        </div>
      );
    }
  };

  // Render permission badges
  const renderPermissionBadges = () => {
    if (admin.role === "root") {
      return <Badge variant="error">Full System Access</Badge>;
    }

    if (!admin.accessControl) {
      return <Badge variant="default">No Permissions Set</Badge>;
    }

    const enabledModules = Object.entries(admin.accessControl || {})
      .filter(([_, config]) => config.enabled)
      .map(([module, _]) => module);

    if (enabledModules.length === 0) {
      return <Badge variant="default">No Access</Badge>;
    }

    return enabledModules.slice(0, 3).map((module) => (
      <Badge key={module} variant="info" className="mr-1">
        {MODULE_LABELS[module] || module}
      </Badge>
    ));
  };

  return (
    <div
      className={`bg-white border rounded-lg shadow-sm transition-all duration-200 ${
        selected
          ? "border-primary-light ring-1 shadow-lg shadow-primary ring-primary"
          : "border-gray-200"
      } w-full mb-4`}
    >
      {/* Card Header */}
      <div
        className={`cursor-pointer rounded-t-lg shadow-sm hover:shadow-md transition-shadow duration-200 ${
          isExpanded ? "bg-gray-50" : "bg-white"
        }`}
        onClick={toggleExpanded}
        role="button"
        aria-expanded={isExpanded}
        aria-controls={`admin-details-${admin.adminCode}`}
      >
        <div className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Left side - Admin info */}
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={`bg-${getRoleColor(
                  admin.role
                )}-100 p-2 rounded-full flex-shrink-0`}
              >
                <ShieldCheckIcon
                  className={`w-5 h-5 text-${getRoleColor(admin.role)}-600`}
                />
              </div>

              <div className="flex flex-col min-w-0">
                <h2 className="text-lg font-bold text-gray-900 truncate max-w-xs">
                  {admin.name}
                </h2>

                <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                  <MailIcon className="w-3.5 h-3.5 text-gray-500" />
                  <span className="truncate max-w-xs">{admin.email}</span>
                </div>

                <div className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                  <UserIcon className="w-3.5 h-3.5 text-gray-500" />
                  <Badge variant={getRoleColor(admin.role)} className="text-xs">
                    {admin.role}
                  </Badge>
                  {admin.rolePreset && admin.rolePreset !== "custom" && (
                    <Badge variant="default" className="text-xs">
                      {admin.rolePreset}
                    </Badge>
                  )}
                  {admin.isBlocked && (
                    <Badge variant="danger" className="text-xs">
                      Blocked
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Middle section - Expansion indicator */}
            <div
              className="order-last sm:order-none flex items-center justify-center sm:mx-2 my-1 sm:my-0"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded();
              }}
            >
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors ${
                  isExpanded ? "bg-gray-100" : ""
                } text-sm text-gray-600`}
              >
                <ChevronDownIcon
                  className={`w-4 h-4 transition-transform duration-300 ${
                    isExpanded ? "rotate-180" : ""
                  }`}
                />
                <span className="font-medium whitespace-nowrap">
                  {isExpanded ? "Hide" : "View"} Details
                </span>
              </div>
            </div>

            {/* Right side - Actions */}
            <div
              className="flex items-center justify-start sm:justify-end"
              onClick={(e) => e.stopPropagation()}
            >
              {renderActionButtons()}
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible content */}
      <div
        id={`admin-details-${admin.adminCode}`}
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="p-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Admin Basic Information */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2">
                <InformationCircleIcon className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-gray-800">
                  Admin Information
                </h3>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <UserIcon className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-gray-500 text-xs">Full Name</p>
                    <p className="font-medium text-gray-800">{admin.name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <MailIcon className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-gray-500 text-xs">Email</p>
                    <p className="font-medium text-gray-800">{admin.email}</p>
                  </div>
                </div>

                {admin.phoneNumber && (
                  <div className="flex items-center gap-2">
                    <PhoneIcon className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-gray-500 text-xs">Phone</p>
                      <p className="font-medium text-gray-800">
                        {admin.phoneNumber}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <LockClosedIcon className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-gray-500 text-xs">Admin Code</p>
                    <p className="font-medium text-gray-800 font-mono">
                      {admin.adminCode}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Account & Access Information */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2">
                <ShieldCheckIcon className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-gray-800">Access & Status</h3>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <KeyIcon className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-gray-500 text-xs">Role & Preset</p>
                    <div className="flex gap-2 mt-1">
                      <Badge variant={getRoleColor(admin.role)}>
                        {admin.role}
                      </Badge>
                      {admin.rolePreset && (
                        <Badge variant="default">{admin.rolePreset}</Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Branch Access - Updated */}
                <div className="flex items-center gap-2">
                  <OfficeBuildingIcon className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-gray-500 text-xs">Branch Access</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="font-medium text-gray-800">
                        {getBranchAccessSummary()}
                      </p>
                      {isRootAdmin &&
                        admin.role !== "root" &&
                        openBranchAccessModal && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openBranchAccessModal(admin)}
                            className="py-0.5 px-2 text-xs"
                          >
                            Manage
                          </Button>
                        )}
                    </div>
                    {admin.branchAccess?.secondaryBranches?.length > 0 && (
                      <p className="text-xs text-gray-600 mt-1">
                        Secondary:{" "}
                        {admin.branchAccess.secondaryBranches
                          .slice(0, 3)
                          .map((b) =>
                            typeof b === "string" ? b : b.branchCode
                          )
                          .join(", ")}
                        {admin.branchAccess.secondaryBranches.length > 3 &&
                          ` +${
                            admin.branchAccess.secondaryBranches.length - 3
                          } more`}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <CogIcon className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-gray-500 text-xs">Permissions</p>
                    <p className="font-medium text-gray-800">
                      {getEnabledModulesCount()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <ExclamationCircleIcon className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-gray-500 text-xs">Account Status</p>
                    <div className="mt-1">
                      {admin.isBlocked ? (
                        <Badge variant="danger">Blocked</Badge>
                      ) : admin.passwordResetRequested ? (
                        <Badge variant="warning">
                          Password Reset Requested
                        </Badge>
                      ) : (
                        <Badge variant="success">Active</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions Overview */}
          {admin.role !== "root" && admin.accessControl && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <KeyIcon className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold text-gray-800">Module Access</h3>
                </div>
                {isRootAdmin && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openPermissionModal(admin)}
                  >
                    Manage Permissions
                  </Button>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex flex-wrap gap-2">
                  {renderPermissionBadges()}
                  {Object.entries(admin.accessControl || {}).filter(
                    ([_, config]) => config.enabled
                  ).length > 3 && (
                    <Badge variant="default">
                      +
                      {Object.entries(admin.accessControl || {}).filter(
                        ([_, config]) => config.enabled
                      ).length - 3}{" "}
                      more
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCard;
